// URL-synced filter & sort state for the v2 grid views.
//
// Single source of truth lives in the query string so deep links share
// state (a teammate can paste a URL with ?q=... &sort=updated and land
// on the same view). Mirrors the shape of api.list() so the view can
// pass the result straight through.

import { useSearchParams } from 'react-router-dom';

export type SortKey = 'name' | 'recent' | 'updated';

export interface GridFilters {
  query: string;
  tag: string | null;
  favOnly: boolean;
  treatment: string | null;
  sort: SortKey;
}

export interface GridFilterControls {
  filters: GridFilters;
  setQuery(q: string): void;
  setTag(t: string | null): void;
  setFavOnly(b: boolean): void;
  setTreatment(t: string | null): void;
  setSort(s: SortKey): void;
  clear(): void;
}

const DEFAULT_SORT: SortKey = 'name';

export function useGridFilters(): GridFilterControls {
  const [params, setParams] = useSearchParams();

  const filters: GridFilters = {
    query: params.get('q') ?? '',
    tag: params.get('tag'),
    favOnly: params.get('fav') === '1',
    treatment: params.get('treatment'),
    sort: (params.get('sort') as SortKey | null) ?? DEFAULT_SORT
  };

  function update(mutate: (next: URLSearchParams) => void) {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  }

  return {
    filters,
    setQuery: (q) =>
      update((next) => {
        if (q.trim()) next.set('q', q.trim());
        else next.delete('q');
      }),
    setTag: (t) =>
      update((next) => {
        if (t) next.set('tag', t);
        else next.delete('tag');
      }),
    setFavOnly: (b) =>
      update((next) => {
        if (b) next.set('fav', '1');
        else next.delete('fav');
      }),
    setTreatment: (t) =>
      update((next) => {
        if (t) next.set('treatment', t);
        else next.delete('treatment');
      }),
    setSort: (s) =>
      update((next) => {
        if (s === DEFAULT_SORT) next.delete('sort');
        else next.set('sort', s);
      }),
    clear: () =>
      update((next) => {
        for (const k of ['q', 'tag', 'fav', 'treatment']) next.delete(k);
      })
  };
}

export function sortContacts<T extends { displayName: string; createdAt: string; updatedAt: string }>(
  items: T[],
  sort: SortKey
): T[] {
  const arr = [...items];
  arr.sort((a, b) => {
    switch (sort) {
      case 'recent':
        return b.createdAt.localeCompare(a.createdAt);
      case 'updated':
        return b.updatedAt.localeCompare(a.updatedAt);
      case 'name':
      default:
        return a.displayName.localeCompare(b.displayName);
    }
  });
  return arr;
}
